// About.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./About.css";
import heroArm from "../../assests/aboutHeroArm.png";
import capWebMobile from "../../assests/web_and_Mobile_Engineering.png";
import capCloudDevops from "../../assests/cloud_and_devops.webp";
import capAIAutomation from "../../assests/automation_and_ai.jpg";
import capITConsulting from "../../assests/consulting_and_traing.jpg";

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const INDUSTRIES = ["Healthcare", "Retail", "Education", "Finance", "Manufacturing", "Logistics"];

const CAPABILITIES = [
  {
    bg: capWebMobile,
    title: "Web & Mobile Engineering",
    desc: "Scalable web platforms and cross-platform mobile apps built on modern, production-ready stacks — from customer-facing storefronts to internal tooling.",
    tags: ["React", "Next.js", "React Native", "Node.js"],
  },
  {
    bg: capCloudDevops,
    title: "Cloud & DevOps",
    desc: "Managed infrastructure, CI/CD pipelines and 24×7 monitored uptime, so releases ship without downtime and scale under real traffic.",
    tags: ["AWS", "Docker", "CI/CD", "Monitoring"],
  },
  {
    bg: capAIAutomation,
    title: "AI & Automation",
    desc: "Applied machine learning and workflow automation that removes repetitive manual work from real business operations, not just proof-of-concept demos.",
    tags: ["ML Pipelines", "RPA", "Chatbots", "Data Automation"],
  },
  {
    bg: capITConsulting,
    title: "IT Consulting & Training",
    desc: "Technical advisory paired with structured, job-ready upskilling programs — built and taught by the same engineers who ship our client work.",
    tags: ["Consulting", "Bootcamps", "Mentorship", "Placements"],
  },
];

const WHY = [
  { title: "8+ years of consistent delivery", desc: "Every engagement shipped on the agreed timeline, with no surprise scope creep." },
  { title: "Dedicated project managers", desc: "A single point of contact who knows your project end to end." },
  { title: "Documented process", desc: "Discovery, architecture, sprints and handover — all tracked and shared with you." },
  { title: "Support beyond launch", desc: "Bug fixes, monitoring and iteration continue after go-live, not just before it." },
];

const MISSION_VISION = [
  {
    label: "Our Mission",
    text: "Deliver hands-on, industry-relevant learning, provide complete mentorship for internships, projects, and research, and enable students and businesses with digital transformation services.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="12" cy="12" r="1" />
      </svg>
    ),
  },
  {
    label: "Our Vision",
    text: "To bridge education and industry through practical technology training and innovative IT solutions, and to be the most trusted software development and student mentoring organization in Nagercoil.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
];

export default function About() {
  const navigate = useNavigate();
  const [heroRef, heroIn] = useInView(0.1);
  const [storyRef, storyIn] = useInView(0.15);
  const [capRef, capIn] = useInView(0.1);
  const [ctaRef, ctaIn] = useInView(0.2);
  const [imgError, setImgError] = useState(false);

  return (
    <div className="au-page">

      {/* ── 1. HERO ── */}
      <section className={`au-hero${heroIn ? " au-in" : ""}`} ref={heroRef}>
        <div className="au-hero__blob au-hero__blob--1" aria-hidden="true" />
        <div className="au-hero__blob au-hero__blob--2" aria-hidden="true" />
        <div className="au-hero__dust" aria-hidden="true">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} style={{ animationDelay: `${i * 0.4}s` }} />
          ))}
        </div>

        <div className="au-hero__grid">
          <div className="au-hero__visual">
            {!imgError ? (
              <img
                src={heroArm}
                alt="Digital network illustration"
                className="au-hero__img"
                onError={() => setImgError(true)}
              />
            ) : (
              <div className="au-hero__img-fallback" aria-hidden="true" />
            )}
          </div>

          <div className="au-hero__content">
            <h1 className="au-hero__heading au-anim" style={{ animationDelay: "0.15s" }}>
              Engineering Growth<br />
              Through <span className="au-accent">Smart Technology.</span>
            </h1>
            <p className="au-hero__sub au-anim" style={{ animationDelay: "0.25s" }}>
              SkillSync Pro Pvt Ltd is a Nagercoil-based IT solutions and training
              company. We design, build and support software — and we train the
              people who go on to build the next generation of it — for{" "}
              <strong>Healthcare, Retail, Education, Finance and Enterprise</strong> clients.
            </p>

            <div className="au-hero__industries au-anim" style={{ animationDelay: "0.35s" }}>
              {INDUSTRIES.map((ind, i) => (
                <span key={ind} className="au-chip" style={{ animationDelay: `${0.4 + i * 0.06}s` }}>{ind}</span>
              ))}
            </div>

            <div className="au-hero__actions au-anim" style={{ animationDelay: "0.45s" }}>
              <button className="au-btn-primary" onClick={() => navigate("/contact")}>
                Get In Touch
                <span className="au-btn-arrow">→</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. WHO WE ARE ── */}
      <section className={`au-story${storyIn ? " au-in" : ""}`} ref={storyRef}>
        <div className="au-story__inner">
          <div className="au-story__text">
            <span className="au-eyebrow">Who We Are</span>
            <h2 className="au-heading">
              An engineering-led company, not just a service vendor
            </h2>
            <p className="au-story__body">
              SkillSync Pro Pvt Ltd combines software engineering discipline with a
              training practice that has placed 75+ students into industry roles —
              giving us a rare view into both sides of the technology workforce.
              We don't outsource core development, and we don't hand you off to a
              junior team after the pitch — the engineers who scope your project
              are the ones who build it.
            </p>
          </div>

          <div className="au-story__stats">
            <div className="au-stat" style={{ transitionDelay: "0.05s" }}><span className="au-stat__num">8+</span><span className="au-stat__label">Years Operating</span></div>
            <div className="au-stat" style={{ transitionDelay: "0.15s" }}><span className="au-stat__num">75+</span><span className="au-stat__label">Students Placed</span></div>
            <div className="au-stat" style={{ transitionDelay: "0.25s" }}><span className="au-stat__num">28+</span><span className="au-stat__label">Active Partners</span></div>
            <div className="au-stat" style={{ transitionDelay: "0.35s" }}><span className="au-stat__num">40+</span><span className="au-stat__label">Projects Shipped</span></div>
          </div>
        </div>

        <div className="au-mv">
          {MISSION_VISION.map((m, i) => (
            <div key={m.label} className="au-mv__item" style={{ transitionDelay: `${i * 0.12}s` }}>
              <span className="au-mv__icon">{m.icon}</span>
              <h4 className="au-mv__label">{m.label}</h4>
              <p className="au-mv__text">{m.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 3. CAPABILITIES (case-study style cards) ── */}
      <section className="au-capabilities" ref={capRef}>
        <div className="au-cap__head">
          <span className="au-eyebrow">What We Do</span>
          <h2 className="au-heading">Core capabilities</h2>
          <p className="au-cap__intro">
            Four practice areas, one team — so your product, infrastructure and
            people all move in the same direction.
          </p>
        </div>

        <div className={`au-cap-grid${capIn ? " au-in" : ""}`}>
          {CAPABILITIES.map((c, i) => (
            <div key={c.title} className="au-cap-card" style={{ transitionDelay: `${i * 0.09}s` }}>
              <img src={c.bg} alt={c.title} className="au-cap-card__media" />

              <div className="au-cap-card__panel">
                <span className="au-cap-card__tag">Service</span>
                <h3 className="au-cap-card__heading">
                  {c.title}
                  <svg className="au-cap-card__arrow" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5l11 7-11 7V5z" />
                  </svg>
                </h3>

                <div className="au-cap-card__reveal">
                  <p className="au-cap-card__desc">{c.desc}</p>
                  <div className="au-cap-card__tags">
                    {c.tags.map((t) => <span key={t} className="au-tag">{t}</span>)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="au-why">
          <span className="au-eyebrow">Why Clients Stay</span>
          <ul className={`au-why__list${capIn ? " au-in" : ""}`}>
            {WHY.map((w, i) => (
              <li key={w.title} className="au-why__item" style={{ transitionDelay: `${0.1 + i * 0.08}s` }}>
                <span className="au-why__check">✓</span>
                <div>
                  <strong className="au-why__title">{w.title}</strong>
                  <p className="au-why__desc">{w.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── 4. CTA ── */}
      <section className={`au-cta${ctaIn ? " au-in" : ""}`} ref={ctaRef}>
        <div className="au-cta__glow" aria-hidden="true" />
        <h2 className="au-cta__heading">Let's discuss your next project</h2>
        <p className="au-cta__sub">
          Whether it's a new product, a cloud migration, or upskilling your team —
          our team responds to every enquiry within one business day.
        </p>
        <div className="au-cta__actions">
          <button className="au-cta__btn" onClick={() => navigate("/contact")}>
            Contact Us <span className="au-btn-arrow">→</span>
          </button>
          <span className="au-cta__location">Nagercoil, Tamil Nadu, India</span>
        </div>
      </section>

    </div>
  );
}